import { Component, computed, inject, Injectable, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import {
    FilterChip,
    Page,
    PageResult,
    QuerySortDirection,
    ReactiveSearchHomeComponent,
    SearchService,
    ServiceHandler,
} from '@nucleus/ui-base';
import {
    CapabilityControllerService,
    CapabilityLifecycleControllerService,
    CapabilityRecord,
} from '../../api/capability-service';
import { DecommissionConfirmRequest } from '../../api/capability-service/model/decommissionConfirmRequest';
import { CapabilityCreateDialogComponent } from './capability-create-dialog.component';
import { CapabilityDecommissionDialogComponent } from './capability-decommission-dialog.component';

/**
 * Admin list page for the capability registry. Phase 2a — reads from
 * <code>GET /capabilities</code> + lifecycle action buttons calling the
 * backend. Detail / new-capability / field-CRUD ship in Phase 2b.
 *
 * <p>Permission gate: {@code CAPABILITY_MANAGEMENT} OR {@code ROLE_ADMIN}
 * (TASK-193 seeded the per-screen authority for 7 admin/test users).
 *
 * <p>Migrated to <code>&lt;lib-list-page&gt;</code> + {@link ReactiveSearchHomeComponent}
 * to share the standard tasksense list-page chrome (toolbar, omnibar,
 * chips, drawer, paginator). Backend has no server-side filter endpoint
 * for capabilities, so {@link CapabilitySearchAdapter} wraps the
 * controller's plain <code>list()</code> and applies the filter +
 * pagination + sort client-side.
 */

type CapStatus = CapabilityRecord.StatusEnum;
type CapStatusOrAll = CapStatus | 'ALL';

/**
 * Filter envelope for the capabilities list. {@code value} is the omnibar
 * free-text (matches code or version, case-insensitive); {@code status}
 * narrows to a single lifecycle state (<code>'ALL'</code> = no narrowing).
 */
export class CapabilityFilter {
    constructor(
        public value: string = '',
        public status: CapStatusOrAll = 'ALL',
    ) {}
}

/**
 * Client-side adapter that lets {@link CapabilitiesComponent} extend
 * {@link ReactiveSearchHomeComponent}. The capability-service exposes
 * only a plain {@code GET /capabilities} (no server-side filter) so the
 * adapter fetches the full list once per refresh and applies the filter,
 * sort and pagination locally. The dataset is small (admin-managed
 * registry), so this is acceptable until / unless a server-side filter
 * endpoint is added.
 */
@Injectable({ providedIn: 'root' })
export class CapabilitySearchAdapter implements SearchService<CapabilityRecord, CapabilityFilter> {

    private readonly api: CapabilityControllerService = inject(CapabilityControllerService);

    search(handler: ServiceHandler<CapabilityRecord[]>): void {
        handler.onStart?.();
        this.api.list().subscribe({
            next:     rows => handler.onSuccess(rows ?? []),
            error:    err  => handler.onError?.(err),
            complete: ()   => handler.onComplete?.(),
        });
    }

    filter(handler: ServiceHandler<PageResult<CapabilityRecord>>, filter: CapabilityFilter, page?: Page): void {
        handler.onStart?.();
        this.api.list().subscribe({
            next: rows => {
                const sortField = page?.sortField ?? 'code';
                const sortAsc   = (page?.sortDirection ?? QuerySortDirection.ASC) === QuerySortDirection.ASC;
                const matched   = (rows ?? [])
                    .filter(c => this.matches(c, filter))
                    .sort((a, b) => this.compare(a, b, sortField, sortAsc));
                const total = matched.length;
                const size  = page?.pageSize  ?? (total || 1);
                const start = (page?.pageNumber ?? 0) * size;
                const slice = page ? matched.slice(start, start + size) : matched;
                handler.onSuccess(new PageResult<CapabilityRecord>(slice, total, page?.pageNumber ?? 0));
            },
            error:    err => handler.onError?.(err),
            complete: ()  => handler.onComplete?.(),
        });
    }

    private matches(c: CapabilityRecord, f: CapabilityFilter): boolean {
        if (f.status && f.status !== 'ALL' && c.status !== f.status) return false;
        const search = (f.value ?? '').trim().toLowerCase();
        if (!search) return true;
        return (c.code ?? '').toLowerCase().includes(search)
            || (c.version ?? '').toLowerCase().includes(search);
    }

    private compare(a: CapabilityRecord, b: CapabilityRecord, field: string, asc: boolean): number {
        // Secondary sort by version for stable ordering when codes tie.
        const primary = String((a as any)[field] ?? '').localeCompare(String((b as any)[field] ?? ''));
        if (primary !== 0) return asc ? primary : -primary;
        const tie = String(a.version ?? '').localeCompare(String(b.version ?? ''));
        return asc ? tie : -tie;
    }
}

@Component({
    selector: 'app-capabilities',
    templateUrl: './capabilities.component.html',
    styleUrl: './capabilities.component.css',
    standalone: false,
})
export class CapabilitiesComponent
    extends ReactiveSearchHomeComponent<CapabilityRecord, CapabilityFilter>
    implements OnInit {

    private readonly capabilityService: CapabilityControllerService = inject(CapabilityControllerService);
    private readonly lifecycleService:  CapabilityLifecycleControllerService = inject(CapabilityLifecycleControllerService);
    protected override router: Router = inject(Router);
    protected override dialog: MatDialog = inject(MatDialog);

    readonly statusOptions: Array<{ value: CapStatusOrAll; label: string }> = [
        { value: 'ALL',                                          label: 'All' },
        { value: CapabilityRecord.StatusEnum.InProgress,         label: 'In progress' },
        { value: CapabilityRecord.StatusEnum.Active,             label: 'Active' },
        { value: CapabilityRecord.StatusEnum.Inactive,           label: 'Inactive' },
        { value: CapabilityRecord.StatusEnum.Decommissioned,     label: 'Decommissioned' },
    ];

    displayedColumns = ['code', 'version', 'status', 'description', 'createdBy', 'createdAt', 'actions'];

    constructor() {
        super(inject(CapabilitySearchAdapter), 'code', QuerySortDirection.ASC);
    }

    // ── ReactiveSearchHomeComponent overrides ───────────────────────────────

    override createFilter(): CapabilityFilter {
        return new CapabilityFilter();
    }

    override onResetFilter(): void {
        this.filter.update(f => Object.assign(f, new CapabilityFilter()));
    }

    /** Gate the auto-fire on permission so the base's ngAfterViewInit
     *  doesn't issue a request the user can't see (showAccessDeny will
     *  have already navigated away). */
    override isReadyForSearch(): boolean {
        return this.permissionChecker.hasAccessToCapabilityManagement();
    }

    ngOnInit(): void {
        if (!this.permissionChecker.hasAccessToCapabilityManagement()) {
            this.showAccessDeny();
        }
    }

    // ── filter wiring ────────────────────────────────────────────────────────

    setStatusFilter(value: CapStatusOrAll): void {
        this.filter.update(f =>
            Object.assign(Object.create(Object.getPrototypeOf(f)), f, { status: value }));
    }

    activeFilterChips = computed<FilterChip[]>(() => {
        const f = this.filter();
        const chips: FilterChip[] = [];
        if (f.status && f.status !== 'ALL') {
            const label = this.statusOptions.find(o => o.value === f.status)?.label ?? f.status;
            chips.push({ key: 'status', icon: 'flag', label: `Status: ${label}`, removable: true });
        }
        return chips;
    });

    hasRemovableChips(): boolean {
        return this.activeFilterChips().some(c => c.removable);
    }

    removeFilterChip(key: string): void {
        if (key === 'status') this.setStatusFilter('ALL');
    }

    // ── permission + status helpers ──────────────────────────────────────────

    canManage(): boolean {
        return this.permissionChecker.hasAccessToManageCapabilities();
    }

    statusClass(status: CapStatus | undefined): string {
        switch (status) {
            case CapabilityRecord.StatusEnum.Active:         return 'status-pill status-active';
            case CapabilityRecord.StatusEnum.InProgress:     return 'status-pill status-inprogress';
            case CapabilityRecord.StatusEnum.Inactive:       return 'status-pill status-inactive';
            case CapabilityRecord.StatusEnum.Decommissioned: return 'status-pill status-decommissioned';
            default:                                          return 'status-pill';
        }
    }

    isInProgress(c: CapabilityRecord): boolean { return c.status === CapabilityRecord.StatusEnum.InProgress; }
    isActive(c: CapabilityRecord):     boolean { return c.status === CapabilityRecord.StatusEnum.Active; }
    isInactive(c: CapabilityRecord):   boolean { return c.status === CapabilityRecord.StatusEnum.Inactive; }

    // ── lifecycle actions (preserved verbatim except using refreshData()) ────

    /** IN_PROGRESS only → ACTIVE. */
    promote(c: CapabilityRecord): void {
        if (!this.canManage()) { this.showAccessDeny(); return; }
        if (!this.isInProgress(c)) return;
        if (!confirm(`Promote ${c.code} ${c.version} from IN_PROGRESS to ACTIVE? (this freezes the version)`)) return;
        this.lifecycleService.promote(c.code, c.version).subscribe({
            next:    () => { this.showSuccess(`Promoted ${c.code} ${c.version}`); this.refreshData(true); },
            error:   (e) => { this.showError(e, 'Promote failed'); },
        });
    }

    /** ACTIVE only → INACTIVE. */
    deactivate(c: CapabilityRecord): void {
        if (!this.canManage()) { this.showAccessDeny(); return; }
        if (!this.isActive(c)) return;
        if (!confirm(`Deactivate ${c.code} ${c.version}? Existing refs keep working; no new refs allowed.`)) return;
        this.lifecycleService.deactivate(c.code, c.version).subscribe({
            next:    () => { this.showSuccess(`Deactivated ${c.code} ${c.version}`); this.refreshData(true); },
            error:   (e) => { this.showError(e, 'Deactivate failed'); },
        });
    }

    /** INACTIVE only → DECOMMISSIONED. Opens the Phase 2b dry-run dialog
     *  which shows dependent schedules + lets the operator pick per-schedule
     *  actions, then closes with the action list this method confirms. */
    decommission(c: CapabilityRecord): void {
        if (!this.canManage()) { this.showAccessDeny(); return; }
        if (!this.isInactive(c)) return;
        const ref = this.dialog.open(CapabilityDecommissionDialogComponent, {
            data: { code: c.code, version: c.version },
            panelClass: 'bw-dialog-panel',
            width: '720px',
            disableClose: true,
        });
        ref.afterClosed().subscribe(actions => {
            if (!actions) return;
            if (!this.permissionChecker.hasAccessToCapabilityManagement()) { this.showAccessDeny(); return; }
            const body: DecommissionConfirmRequest = { schedules: actions };
            this.lifecycleService.decommissionConfirm(c.code, c.version, body).subscribe({
                next:    () => { this.showSuccess(`Decommissioned ${c.code} ${c.version}`); this.refreshData(true); },
                error:   (e) => { this.showError(e, 'Decommission failed'); },
            });
        });
    }

    openCreateDialog(): void {
        if (!this.canManage()) { this.showAccessDeny(); return; }
        const ref = this.dialog.open(CapabilityCreateDialogComponent, {
            panelClass: 'bw-dialog-panel',
            width: '560px',
            disableClose: true,
        });
        ref.afterClosed().subscribe(created => { if (created) this.refreshData(true); });
    }

    openDetail(c: CapabilityRecord): void {
        this.router.navigate(['/administration/capabilities', c.code, c.version]);
    }

    /** IN_PROGRESS only — hard delete (draft never published). */
    deleteDraft(c: CapabilityRecord): void {
        if (!this.canManage()) { this.showAccessDeny(); return; }
        if (!this.isInProgress(c)) return;
        if (!confirm(`Delete draft ${c.code} ${c.version}? Field rows cascade.`)) return;
        this.capabilityService._delete(c.code, c.version).subscribe({
            next:    () => { this.showSuccess(`Deleted draft ${c.code} ${c.version}`); this.refreshData(true); },
            error:   (e) => { this.showError(e, 'Delete failed'); },
        });
    }
}
